import { filtrerRecherche } from '@imp/shared/recherche';
import { useQuery } from '@tanstack/react-query';
import { FileText, MessageSquare, Settings2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { contactDisplayName, listContacts } from '@/modules/clients/api';
import { RelancesDevis } from '@/modules/messagerie/RelancesDevis';
import { RelancesSettings } from '@/modules/messagerie/RelancesSettings';
import { SmsThread } from '@/modules/messagerie/SmsThread';
import { listConversations, type SmsConversation } from '@/modules/messagerie/api';

function quand(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

export function MessageriePage() {
  const [vue, setVue] = useState<'fil' | 'relances' | 'reglages'>('fil');
  const [selId, setSelId] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const convQ = useQuery({
    queryKey: ['sms-conversations'],
    queryFn: listConversations,
    refetchInterval: 20000,
  });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const contactMap = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );

  const nom = (c: SmsConversation): string => {
    const ct = c.contact_id ? contactMap.get(c.contact_id) : undefined;
    return ct ? contactDisplayName(ct) : c.telephone;
  };

  const conversations = useMemo(() => convQ.data ?? [], [convQ.data]);
  // Recherche universelle : nom, numéro et dernier message.
  const filtered = useMemo(
    () =>
      filtrerRecherche(conversations, q, (c) => [
        nom(c),
        c.telephone,
        c.dernier_message_extrait,
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversations, q, contactMap],
  );

  const selected = conversations.find((c) => c.id === selId) ?? null;

  if (vue === 'relances') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Relances devis</h1>
            <p className="text-muted-foreground">
              Devis envoyés et suite donnée. Relancer un client est un acte de communication : il a
              sa place ici, aux côtés des échanges et des réglages.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setVue('fil');
            }}
          >
            <MessageSquare className="h-4 w-4" /> Messagerie
          </Button>
        </div>
        <RelancesDevis />
      </div>
    );
  }

  if (vue === 'reglages') {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Réglages des relances</h1>
            <p className="text-muted-foreground">
              Déclencheurs, créneau d&apos;envoi et messages types.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setVue('fil');
            }}
          >
            <MessageSquare className="h-4 w-4" /> Messagerie
          </Button>
        </div>
        <RelancesSettings />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Messagerie</h1>
          <p className="text-muted-foreground">SMS clients — échanges et relances automatiques.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setVue('relances');
            }}
          >
            <FileText className="h-4 w-4" /> Relances devis
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setVue('reglages');
            }}
          >
            <Settings2 className="h-4 w-4" /> Réglages des relances
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(260px,340px)_1fr]">
        {/* Liste des conversations */}
        <Card className="flex max-h-[70vh] flex-col overflow-hidden">
          <div className="border-b p-3">
            <BarreRecherche valeur={q} onChange={setQ} quoi="une conversation" />
          </div>
          <div className="flex-1 overflow-y-auto">
            {convQ.isLoading ? (
              <p className="text-muted-foreground p-4 text-sm">Chargement…</p>
            ) : filtered.length === 0 ? (
              <p className="text-muted-foreground p-4 text-sm">
                {conversations.length === 0
                  ? 'Aucune conversation. Elles apparaîtront ici dès le premier SMS envoyé ou reçu.'
                  : 'Aucun résultat.'}
              </p>
            ) : (
              <ul>
                {filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelId(c.id);
                      }}
                      className={`hover:bg-muted/50 flex w-full items-start gap-3 border-b px-3 py-3 text-left ${
                        selId === c.id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate font-medium">{nom(c)}</span>
                          <span className="text-muted-foreground shrink-0 text-[11px]">
                            {quand(c.dernier_message_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-muted-foreground truncate text-xs">
                            {c.dernier_message_sens === 'sortant' ? 'Vous : ' : ''}
                            {c.dernier_message_extrait ?? c.telephone}
                          </p>
                        </div>
                        {c.statut === 'opt_out' ? (
                          <span className="bg-destructive/10 text-destructive mt-0.5 inline-block rounded px-1.5 text-[10px]">
                            Désinscrit
                          </span>
                        ) : null}
                      </div>
                      {c.non_lus > 0 ? (
                        <span className="bg-primary text-primary-foreground mt-0.5 shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                          {c.non_lus}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Fil sélectionné */}
        <Card className="flex max-h-[70vh] min-h-[420px] flex-col overflow-hidden">
          {selected ? (
            <>
              <div className="flex items-center justify-between gap-2 border-b p-3">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{nom(selected)}</p>
                  <p className="text-muted-foreground text-xs">{selected.telephone}</p>
                </div>
                <div className="flex items-center gap-1">
                  {selected.contact_id ? (
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/clients/${selected.contact_id}`}>Fiche client</Link>
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelId(null);
                    }}
                    aria-label="Fermer"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden p-3">
                <SmsThread
                  conversationId={selected.id}
                  telephone={selected.telephone}
                  contactId={selected.contact_id}
                  optOut={selected.statut === 'opt_out'}
                />
              </div>
            </>
          ) : (
            <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
              <MessageSquare className="h-8 w-8" />
              <p>Sélectionnez une conversation pour afficher l&apos;échange.</p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
